
import { useState, useEffect } from 'react';

type Theme = 'light' | 'dark' | 'system';

export const useDarkMode = (): [Theme, (theme: Theme) => void] => {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window === 'undefined') {
      return 'system';
    }
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme === 'light' || storedTheme === 'dark' || storedTheme === 'system') {
      return storedTheme;
    }
    return 'system';
  });

  useEffect(() => {
    const root = window.document.documentElement;

    // A function to apply the theme based on the current state
    const applyResolvedTheme = () => {
      const isDark =
        theme === 'dark' ||
        (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
      
      if (isDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    };

    // Apply the theme whenever the state changes
    applyResolvedTheme();
    
    // Persist the theme choice to local storage
    localStorage.setItem('theme', theme);

    // Set up a listener for changes in the OS theme preference
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    // This handler will re-run the theme application logic if the theme is set to 'system'
    const handleSystemThemeChange = () => {
      if (localStorage.getItem('theme') === 'system') {
        applyResolvedTheme();
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);

    // Clean up the listener when the component unmounts or the theme dependency changes
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [theme]); // This effect re-runs every time the user selects a new theme

  return [theme, setTheme];
};
